// FulcrumAPI.js - Handles creating Fulcrum apps via API with improved error handling but keeping the original structure
import { trackMixPanelEvent } from './ApiService';

const REGION_BASE_URLS = {
  us: 'https://api.fulcrumapp.com',
  eu: 'https://api.fulcrumapp-eu.com',
  ca: 'https://api.fulcrumapp-ca.com',
  au: 'https://api.fulcrumapp-au.com',
};

const REGION_WEB_URLS = {
  us: 'https://web.fulcrumapp.com',
  eu: 'https://web.fulcrumapp-eu.com',
  ca: 'https://web.fulcrumapp-ca.com',
  au: 'https://web.fulcrumapp-au.com',
};

export const REGION_OPTIONS = [
  { value: 'us', label: 'US (api.fulcrumapp.com)' },
  { value: 'eu', label: 'EU (api.fulcrumapp-eu.com)' },
  { value: 'ca', label: 'CA (api.fulcrumapp-ca.com)' },
  { value: 'au', label: 'AU (api.fulcrumapp-au.com)' },
];

export function getBaseUrl(region = 'us') {
  return `${REGION_BASE_URLS[region] ?? REGION_BASE_URLS.us}/api/v2`;
}

/**
 * Create a Fulcrum app via API with comprehensive error handling
 * @param {Array} formFields - Array of form fields
 * @param {string} formName - Name of the form
 * @param {string} formDescription - Description of the form
 * @param {string} apiToken - Fulcrum API token
 * @returns {Promise<Object>} Created app information
 */
export const createFulcrumApp = async (formFields, formName, formDescription, apiToken, region = 'us') => {
  console.log('🚀 Starting Fulcrum app creation...');
  
  // Validate inputs
  if (!apiToken) {
    throw new Error('API token is required');
  }

  if (!formFields || formFields.length === 0) {
    throw new Error('Form must have at least one field');
  }
  
  console.log(`📝 Creating app with ${formFields.length} fields`);
  console.log(`📋 Form name: "${formName}"`);
  console.log(`📄 Form description: "${formDescription}"`);
  
  try {
    // Generate the form structure using our existing function
    console.log('🔧 Generating Fulcrum app structure...');
    const fulcrumApp = generateFulcrumAppStructure(formFields, formName, formDescription);
    
    // Log the structure being sent (truncated for readability)
    const dataStr = JSON.stringify(fulcrumApp, null, 2);
    console.log('📡 Sending to Fulcrum API:');
    console.log(dataStr.substring(0, 500) + (dataStr.length > 500 ? '...' : ''));

    const url = `${getBaseUrl(region)}/forms.json`;
    
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-ApiToken': apiToken
      },
      body: JSON.stringify(fulcrumApp)
    };

    // Make the API request with retry logic
    const response = await makeRobustRequest(url, options);
    
    // Parse and validate the response
    const result = await parseAndValidateResponse(response, region, apiToken);
    
    console.log('✅ Fulcrum app created successfully!');
    return result;
    
  } catch (error) {
    console.error('💥 Error creating Fulcrum app:', error);
    throw new Error(`Failed to create Fulcrum app: ${error.message}`);
  }
};

/**
 * Make a robust request with retries and better error handling
 * @param {string} url - API URL
 * @param {Object} options - Fetch options
 * @param {number} maxRetries - Maximum retry attempts
 * @returns {Promise<Response>} The fetch response
 */
async function makeRobustRequest(url, options, maxRetries = 3) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    console.log(`📡 Attempt ${attempt}/${maxRetries}: Making request to Fulcrum API...`);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      console.log(`📥 Fulcrum API response - Status: ${response.status}`);
      
      return response;
      
    } catch (error) {
      console.error(`❌ Attempt ${attempt} failed:`, error.message);
      
      if (error.name === 'AbortError') {
        console.error('⏰ Request timed out after 30 seconds');
      }
      
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Wait before retrying (exponential backoff)
      const waitTime = Math.pow(2, attempt) * 1000;
      console.log(`⏳ Waiting ${waitTime}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
}

/**
 * Parse and validate the Fulcrum API response with better error handling
 * @param {Response} response - The fetch response
 * @returns {Promise<Object>} Parsed response data
 */
async function parseAndValidateResponse(response, region = 'us', apiToken) {
  console.log('🔍 Parsing Fulcrum API response...');
  
  let responseText = '';
  let responseData = null;
  let organizationId = '';
  const debugLoggingEnabled = process.env.REACT_APP_DEBUG === 'true';
  
  try {

    // Get organizationId for MixPanel Tracking
    const userResponse = await fetch(`${getBaseUrl(region)}/users.json`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-ApiToken': apiToken
      }
    });
    


    if (!userResponse.ok) {
      console.warn('⚠️ Failed to fetch user info for organization_id tracking');
      organizationId = '';
    } else {
      try {
        const userData = await userResponse.json();
        organizationId = userData?.user?.current_organization?.id || '';
      } catch (e) {
        console.warn('⚠️ Failed to parse user info response:', e.message);
        organizationId = '';
      }
    }
  } catch (error) {
    console.error('❌ Failed to fetch user info for organization_id:', error);
  }

  try {
    responseText = await response.text();
    console.log(`📄 Response text length: ${responseText.length} characters`);
    
    if (!responseText || responseText.trim() === '') {
      throw new Error('Empty response from Fulcrum API');
    }
    
    console.log(`📄 Response preview: ${responseText.substring(0, 200)}${responseText.length > 200 ? '...' : ''}`);
    
    // Try to parse as JSON
    try {
      responseData = JSON.parse(responseText);
      console.log('✅ Successfully parsed JSON response');

      if (debugLoggingEnabled) {
        console.log(`📋 [REGION=${region}] Full response (status=${response.status}):`, JSON.stringify(responseData, null, 2));
      }

      // Handle error responses
      if (!response.ok) {
        const errorMessage = responseData?.error || responseData?.message || `HTTP ${response.status}`;
        if (debugLoggingEnabled) {
          console.error(`❌ [REGION=${region}] API error body:`, JSON.stringify(responseData, null, 2));
        }
        
        throw new Error(`API Error: ${response.status} - ${errorMessage}`);
      }

      // Validate response structure
      if (!responseData || !responseData.form || !responseData.form.id) {
        console.error('❌ Invalid response structure - missing form ID');
        throw new Error('Invalid response structure from Fulcrum API');
      }
    } catch (parseError) {
      console.error('❌ JSON parsing failed:', parseError.message);
      console.error('📄 Raw response that failed to parse:', responseText.substring(0, 500));
      
      // Check if it's an HTML error page
      if (responseText.includes('<html') || responseText.includes('<!DOCTYPE')) {
        throw new Error('Received HTML error page instead of JSON. The Fulcrum API may be experiencing issues.');
      }
      
      throw new Error(`Invalid JSON response: ${parseError.message}`);
    }

  } catch (error) {
    console.error('❌ Failed to read response:', error);
    trackMixPanelEvent('Instaform Form Created', {
      type: 'token',
      result: 'failure', 
      form_id: null,
      organization_id: organizationId,
      error: error.message
    })
  }

  trackMixPanelEvent('Instaform Form Created', {
    type: 'token',
    result: 'success', 
    form_id: responseData.form.id,
    organization_id: organizationId,
    error: null
  })
  
  const webBase = REGION_WEB_URLS[region] ?? REGION_WEB_URLS.us;
  return {
    success: true,
    formId: responseData.form.id,
    formUrl: `${webBase}/apps/${responseData.form.id}/edit`,
    data: responseData
  };
}

/**
 * Generate the Fulcrum app structure (keeping your original logic)
 * @param {Array} formFields - Form fields array
 * @param {string} formName - Form name
 * @param {string} formDescription - Form description
 * @returns {Object} Fulcrum app structure
 */
const generateFulcrumAppStructure = (formFields, formName, formDescription) => {
  console.log('🔧 Generating Fulcrum app structure...');
  
  // Track used keys to avoid duplicates
  const usedKeys = new Set();
  
  // Generate a unique key for each field (4 character hex)
  const generateKey = () => {
    let key;
    do {
      key = Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    } while (usedKeys.has(key));
    
    usedKeys.add(key);
    return key;
  };

  // Generate data_name from label
  const generateDataName = (label) => {
    if (!label || typeof label !== 'string') {
      return `field_${generateKey()}`;
    }
    return label.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .replace(/_{2,}/g, '_')
      .replace(/^_+|_+$/g, '') || `field_${generateKey()}`;
  };

  // Process fields recursively to handle sections and nesting
  const processField = (field, index) => {
    const label = field.label || "Unnamed Field";
    const dataName = generateDataName(label);
    const key = generateKey();
    
    console.log(`🔄 Processing field ${index + 1}: ${label} (${field.type})`);

    const baseField = {
      key: key,
      label: label,
      description: field.description || null,
      required: field.required || false,
      disabled: field.disabled || false,
      hidden: field.hidden || false,
      data_name: dataName,
      default_value: null,
      visible_conditions_type: null,
      visible_conditions_behavior: "clear",
      visible_conditions: null,
      required_conditions_type: null,
      required_conditions: null,
      default_previous_value: false
    };

    switch (field.type) {
      case "Section":
        return {
          ...baseField,
          type: "Section",
          display: "inline",
          elements: field.elements ? field.elements.map(processField) : []
        };

      case "TextField":
        return {
          ...baseField,
          type: "TextField",
          numeric: false,
          pattern: null,
          pattern_description: null,
          min_length: null,
          max_length: null
        };

      case "NumericField":
        return {
          ...baseField,
          type: "TextField",
          numeric: true,
          format: "decimal",
          min: null,
          max: null,
          min_length: null,
          max_length: null
        };

      case "YesNoField":
        return {
          ...baseField,
          type: "YesNoField",
          positive: { label: "Yes", value: "yes" },
          negative: { label: "No", value: "no" },
          neutral: { label: "N/A", value: "n/a" },
          neutral_enabled: false
        };

      case "DateTimeField":
        return {
          ...baseField,
          type: "DateTimeField"
        };

      case "TimeField":
        return {
          ...baseField,
          type: "TimeField"
        };

      case "ChoiceField":
        const choices = field.choices && Array.isArray(field.choices) && field.choices.length > 0 
          ? field.choices.map(choice => {
              if (typeof choice === 'string') {
                return { label: choice, value: choice };
              }
              return choice;
            })
          : [
              { label: "Option 1", value: "Option 1" },
              { label: "Option 2", value: "Option 2" },
              { label: "Option 3", value: "Option 3" }
            ];
            
        console.log(`🔘 Choice field with ${choices.length} options`);
        return {
          ...baseField,
          type: "ChoiceField",
          multiple: false,
          allow_other: false,
          choices: choices
        };

      case "MultiChoiceField":
        const multiChoices = field.choices && Array.isArray(field.choices) && field.choices.length > 0 
          ? field.choices.map(choice => {
              if (typeof choice === 'string') {
                return { label: choice, value: choice };
              }
              return choice;
            })
          : [
              { label: "Option 1", value: "Option 1" },
              { label: "Option 2", value: "Option 2" },
              { label: "Option 3", value: "Option 3" }
            ];
            
        console.log(`☑️ Multi-choice field with ${multiChoices.length} options`);
        return {
          ...baseField,
          type: "ChoiceField",
          multiple: true,
          allow_other: true,
          choices: multiChoices,
          min_length: null,
          max_length: null
        };

      case "Label":
        return {
          ...baseField,
          type: "Label"
        };

      case "AddressField":
        return {
          ...baseField,
          type: "AddressField",
          auto_populate: true
        };

      case "SignatureField":
        return {
          ...baseField,
          type: "SignatureField",
          agreement_text: ""
        };

      case "PhotoField":
        console.log(`📸 Photo field detected`);
        return {
          ...baseField,
          type: "PhotoField",
          min_length: null,
          max_length: null,
          annotations_enabled: false,
          deidentification_enabled: true,
          timestamp_enabled: false,
          latlongstamp_enabled: false
        };

      default:
        console.warn(`⚠️ Unknown field type: ${field.type}, defaulting to TextField`);
        // Default to TextField for unknown types
        return {
          ...baseField,
          type: "TextField",
          numeric: false,
          pattern: null,
          pattern_description: null,
          min_length: null,
          max_length: null
        };
    }
  };

  // Build the final app structure
  const structure = {
    form: {
      name: formName || 'Auto-Generated Form',
      description: formDescription || 'Automatically generated form',
      elements: formFields.map(processField)
    }
  };
  
  console.log(`✅ Generated structure with ${structure.form.elements.length} elements`);
  return structure;
};
